<script lang="ts">
	import { collection, doc, getFirestore, setDoc, Timestamp } from 'firebase/firestore';
	import _ from 'lodash';
	import { getStorage, ref, uploadBytes } from 'firebase/storage';
	import { v4 } from 'uuid';
	import { goto } from '$app/navigation';
	import { delay, fetchRetry } from '$lib/utils';
	import { PUBLIC_FIRESTORE_DB, PUBLIC_VOICE_ALIGN_API } from '$env/static/public';
	import Header from '$lib/Header.svelte';
	import SpinnerButton from '$lib/components/SpinnerButton.svelte';

	import * as Card from '$lib/shadcn/components/ui/card';
	import { Progress } from '$lib/shadcn/components/ui/progress';
	import * as FileDropZone from '$lib/shadcn/components/ui/file-drop-zone';
	import FileUpload from '$lib/components/FileUpload.svelte';
	import { serviceContext } from '$lib/ServiceContext';
	import SvelteSeo from 'svelte-seo';
	import { resolveLocale, useToast } from '$lib/utils.svelte';
	import { logger } from '@sentry/sveltekit';

	const acceptedFileTypes = [
		'audio/wav',
		'audio/x-wav',
		'audio/mpeg',
		'audio/aiff',
		'audio/x-aiff',
		'audio/flac',
		'audio/x-flac'
	];

	let allowUpload = $state(false);
	let uploading = $state(false);
	let uploaded = $state(false);
	let processing = $state(false);
	let processing_completed = $state(false);

	let progress_string = $state('');

	let referenceVocalFiles = $state<File[]>([]);
	let dubVocalsFiles = $state<File[]>([]);

	$effect(() => {
		if (referenceVocalFiles.length != 1) {
			allowUpload = false;
			return;
		}
		if (dubVocalsFiles.length < 1) {
			allowUpload = false;
			return;
		}
		allowUpload = true;
	});

	async function uploadToBucket(file: File) {
		let storage_ref = ref(getStorage(), `${v4()}-${v4()}`);
		let result = await uploadBytes(storage_ref, await file.arrayBuffer(), {
			contentType: file.type
		});

		return { path: result.ref.fullPath, original_name: file.name };
	}

	async function upload() {
		const referenceVocal = referenceVocalFiles;
		const dubVocals = dubVocalsFiles;

		if ((referenceVocal?.length ?? 0) != 1) {
			return;
		}
		if ((dubVocals?.length ?? 0) < 1) {
			return;
		}

		serviceContext.analyticsService.appUploadFiles(dubVocals.length);
		uploading = true;

		try {
			const referenceVocalPath = await uploadToBucket(referenceVocal![0]);
			const dubVocalPaths = await Promise.all(
				(dubVocals ?? []).map((file) => uploadToBucket(file))
			);
			uploaded = true;
			processing = true;

			let audio_data = collection(getFirestore(PUBLIC_FIRESTORE_DB), 'audio_data');
			let newDoc = doc(audio_data);
			await setDoc(newDoc, {
				id: newDoc.id,
				created_at: Timestamp.fromMillis(Date.now()),
				reference_vocal: referenceVocalPath,
				dub_vocals: dubVocalPaths
			});

			let form = new FormData();
			form.set('id', newDoc.id);
			await fetchRetry(`${PUBLIC_VOICE_ALIGN_API}/`, { method: 'post', body: form });
			while (true) {
				const status = await fetchRetry(`${PUBLIC_VOICE_ALIGN_API}/${newDoc.id}`);
				const text = await status.text();
				progress_string = text;
				if (text === 'completed') {
					processing_completed = true;
					break;
				} else if (text === 'error' || !status.ok) {
					throw 'error during processing';
				}
				await delay(1000);
			}
			processing_completed = true;
			serviceContext.analyticsService.appShowResult();
			await goto(`${resolveLocale('/(webapp)/[[locale=locale]]/results')}?id=${newDoc.id}`);
		} catch {
			progress_string = '';
			uploading = false;
			uploaded = false;
			processing = false;
			(await useToast()).error('Error during processing', {
				description: 'There was an error during processing, please try again.'
			});
		}
	}

	function onFileReject(opts: { file: File; reason: FileDropZone.FileRejectedReason }) {
		logger.info('Rejected file', { file: opts.file, reject_reason: opts.reason });
	}
</script>

<SvelteSeo title="TightStacks - App" />

{#if processing}
	<div
		class="absolute top-0 left-0 z-10 flex h-full w-full items-center justify-center backdrop-blur-xs"
	>
		<Card.Root>
			<Card.Content>
				<div class="flex w-96 flex-col gap-2">
					{#if !processing_completed}
						<p class="text-4xl">Processing...</p>
					{:else}
						<p class="text-4xl">Finished</p>
					{/if}
					<Progress value={processing_completed ? 100 : null} />
					{#if !processing_completed}
						{#if progress_string}
							<p class="">{_.capitalize(progress_string)}... This might take a while..</p>
						{:else}
							<p class="">Please wait...</p>
						{/if}
					{:else}
						<p class="">Redirecting...</p>
					{/if}
				</div>
			</Card.Content>
		</Card.Root>
	</div>
{/if}
<Header />
<main class="flex justify-center p-4">
	<div class="flex w-full max-w-(--breakpoint-lg) flex-col gap-8">
		<div class="flex flex-col gap-2">
			<p class="text-2xl">Select reference vocal</p>
			<FileUpload
				{acceptedFileTypes}
				maxFiles={10}
				bind:files={referenceVocalFiles}
				onFileRejected={onFileReject}
				disabled={uploading}
				maxFileSize={50 * FileDropZone.MEGABYTE}
			/>
		</div>
		<div class="flex flex-col gap-2">
			<p class="text-2xl">Select dub vocals</p>
			<FileUpload
				{acceptedFileTypes}
				maxFiles={10}
				bind:files={dubVocalsFiles}
				onFileRejected={onFileReject}
				disabled={uploading}
				maxFileSize={50 * FileDropZone.MEGABYTE}
			/>
		</div>
		<div class="flex flex-col gap-8">
			<p class="text-muted-foreground">
				By uploading you agree to the processing of the files on our servers according to our <a
					class="anchor"
					href="/privacy">Privacy Policy</a
				>.
			</p>
			<div class="flex flex-col gap-1">
				<SpinnerButton size="lg" variant="filled" action={upload} disabled={!allowUpload}
					>Upload

					{#snippet loading()}
						{#if !uploaded}
							Uploading...
						{:else if processing && !processing_completed}
							Processing...
						{:else}
							Done
						{/if}
					{/snippet}
				</SpinnerButton>
				<p class="text-muted-foreground text-center">
					Are you using Windows or macOS? Then <a
						class="anchor"
						href={resolveLocale('/(webapp)/[[locale=locale]]/download')}>Get the Plugin</a
					> now!
				</p>
			</div>
		</div>
	</div>
</main>
